import type {
  AdminDraw,
  AdminGame,
  AdminProfile,
  AdminRole,
  AdminSession,
  AdminUser,
  AuditEntry,
  Bank,
  Bet,
  BetEditResult,
  BetType,
  Draw,
  DrawSummary,
  GameConfig,
  GameInteraction,
  GameRules,
  HistoryProfile,
  HistoryResult,
  LedgerRow,
  Paged,
  PermissionCatalogue,
  PermissionGrid,
  ReferralCode,
  ReportSummary,
  ResultEditResult,
  RulesGuide,
  RulesGuideDocument,
  Settings,
  SystemStats,
  UpcomingRounds,
  UserStatus,
  WalletTransaction,
} from '../types/api';

import { getValidToken } from './session';

/** The tile's own presentation, shared by creating a game and editing one. */
export interface GameTileChanges {
  name?: string;
  subtitle?: string;
  badge?: string | null;
  tileImage?: string | null;
  /**
   * What tapping the tile does — and, on the API's side, whether it is playable
   * at all. `clickable` is the only value that opens a game.
   */
  interaction?: GameInteraction;
  /** Withhold the tile from the player hall without deleting it. */
  isHidden?: boolean;
  sortOrder?: number;
}

/** The fields an operator may change on a bet. At least one must be present. */
export interface BetChanges {
  position?: number;
  betType?: BetType;
  /** Required when the bet is, or becomes, a number pick. */
  pickedNumber?: number | null;
  stakeCents?: number;
  /**
   * When the bet is recorded as having been placed, as an ISO string.
   *
   * Moves the row in the history and nothing else — the stake, the odds and the
   * outcome are untouched by it.
   */
  createdAt?: string;
}

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:4000/api/v1';

/** An API error carrying the server's machine-readable code. */
export class ApiError extends Error {
  status: number;
  code: string;
  details?: { field: string; message: string }[];

  constructor(status: number, code: string, message: string, details?: ApiError['details']) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

type Query = Record<string, string | number | boolean | undefined | null>;

function buildUrl(path: string, query?: Query) {
  const url = new URL(BASE + path, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/** Fires when the server rejects our token, so the app can bounce to sign-in. */
const unauthorizedListeners = new Set<(code: string) => void>();

/**
 * Reports a refused token from somewhere other than an HTTP response.
 *
 * The live-query layer authenticates over the socket and gets its refusals in a
 * `live.error` frame, which never passes through `request` below. Without this
 * an expired session would leave the console's tables silently frozen while it
 * still looked signed in.
 */
export function notifyUnauthorized(code: string) {
  unauthorizedListeners.forEach((listener) => listener(code));
}

/** Listeners receive the server's code, so "idle" and "revoked" stay distinct. */
export function onUnauthorized(listener: (code: string) => void): () => void {
  unauthorizedListeners.add(listener);
  return () => {
    unauthorizedListeners.delete(listener);
  };
}

async function request<T>(
  method: string,
  path: string,
  options: { body?: unknown; query?: Query; auth?: boolean } = {},
): Promise<T> {
  const { body, query, auth = true } = options;
  const headers: Record<string, string> = { Accept: 'application/json' };

  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (auth) {
    // An expired token is dropped here rather than sent — `getValidToken`
    // clears the session, so the app redirects instead of round-tripping a 401.
    const token = getValidToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'Cannot reach the API. Is it running on port 4000?');
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ApiError(response.status, 'MALFORMED_RESPONSE', 'The API returned a non-JSON response.');
    }
  }

  if (!response.ok) {
    const error = (payload as { error?: { code: string; message: string; details?: ApiError['details'] } })
      ?.error;
    if (response.status === 401) {
      const code = error?.code ?? 'UNAUTHORIZED';
      unauthorizedListeners.forEach((listener) => listener(code));
    }
    throw new ApiError(
      response.status,
      error?.code ?? 'UNKNOWN',
      error?.message ?? `Request failed with ${response.status}`,
      error?.details,
    );
  }

  return payload as T;
}

const get = <T>(path: string, query?: Query) => request<T>('GET', path, { query });
const post = <T>(path: string, body?: unknown) => request<T>('POST', path, { body });
const put = <T>(path: string, body?: unknown) => request<T>('PUT', path, { body });
const patch = <T>(path: string, body?: unknown) => request<T>('PATCH', path, { body });
const del = <T>(path: string, body?: unknown) => request<T>('DELETE', path, { body });

export const api = {
  /* ── Session ─────────────────────────────────────────────────────────── */
  signOut: () => request<{ signedOut: boolean }>('POST', '/auth/logout', {}),
  /**
   * `displacedSessions` is how many sessions this sign-in ended elsewhere.
   *
   * One account holds one session, so anything above zero means these
   * credentials were already in use — on another device, or in another browser.
   * Worth telling the operator: it is the one moment the console can say so.
   */
  signIn: (username: string, password: string) =>
    request<{ token: string; admin: AdminProfile; displacedSessions: number }>(
      'POST',
      '/auth/admin/login',
      { body: { username, password }, auth: false },
    ),

  /* ── Public game data ────────────────────────────────────────────────── */
  rules: () => get<GameRules>('/rules'),
  /**
   * The banks a player may hold an account with.
   *
   * The same list the signup form offers — one endpoint, so the field on a
   * registration and the field an operator corrects it in cannot drift apart.
   */
  banks: () => get<{ items: Bank[] }>('/banks'),
  currentDraw: () => get<Draw>('/draws/current'),
  recentDraws: (limit = 12) => get<{ items: Draw[] }>('/draws/recent', { limit }),

  /* ── Players ─────────────────────────────────────────────────────────── */
  users: (query: Query) => get<Paged<AdminUser>>('/admin/users', query),
  user: (id: string) => get<AdminUser>(`/admin/users/${id}`),
  userTransactions: (id: string, query: Query) =>
    get<Paged<WalletTransaction>>(`/admin/users/${id}/transactions`, query),
  approveUser: (id: string, initialBalanceCents?: number) =>
    post<AdminUser>(`/admin/users/${id}/approve`, { initialBalanceCents }),
  rejectUser: (id: string, reason: string) => post<AdminUser>(`/admin/users/${id}/reject`, { reason }),
  setBalance: (id: string, balanceCents: number, reason: string) =>
    put<{ user: AdminUser; previousCents: number; deltaCents: number }>(
      `/admin/users/${id}/balance`,
      { balanceCents, reason },
    ),

  createUser: (body: {
    username: string;
    password?: string;
    fullName: string;
    phone?: string;
    address?: string;
    bankName?: string;
    bankAccountNo?: string;
    /** The history recipe, set with the account so one dialog does the job. */
    historyProfile?: HistoryProfile;
    /** Run it straight after creating. */
    generateHistory?: boolean;
    status?: UserStatus;
    balanceCents?: number;
  }) =>
    post<AdminUser & { history: HistoryResult | null; historyError: string | null }>(
      '/admin/users',
      body,
    ),
  updateUser: (
    id: string,
    body: {
      fullName?: string;
      phone?: string | null;
      address?: string | null;
      bankName?: string | null;
      bankAccountNo?: string | null;
      status?: UserStatus;
      password?: string;
      /** Replaces the stored recipe wholesale. Null clears it. */
      historyProfile?: HistoryProfile | null;
      reason?: string;
    },
  ) => patch<AdminUser>(`/admin/users/${id}`, body),
  /** Adds an amount. The absolute-figure equivalent is `setBalance`. */
  topUp: (id: string, amountCents: number, reason: string) =>
    post<{ user: AdminUser; previousCents: number; amountCents: number; reference: string }>(
      `/admin/users/${id}/top-up`,
      { amountCents, reason },
    ),
  withdraw: (id: string, amountCents: number, reason: string) =>
    post<{ user: AdminUser; previousCents: number; amountCents: number; reference: string }>(
      `/admin/users/${id}/withdraw`,
      { amountCents, reason },
    ),
  deleteUser: (id: string, reason?: string) =>
    del<{ deleted: boolean; username: string; betsRemoved: number; transactionsRemoved: number }>(
      `/admin/users/${id}`,
      { reason },
    ),
  /**
   * Runs this player's history profile.
   *
   * With no body it runs what is stored on the account. `profile` overrides it
   * for one run without saving, `replace` clears what an earlier run wrote
   * first, and `previewOnly` returns the plan without writing anything.
   */
  generateHistory: (
    id: string,
    body: { profile?: HistoryProfile; replace?: boolean; previewOnly?: boolean } = {},
  ) => post<HistoryResult>(`/admin/users/${id}/history`, body),

  /** Removes what previous runs wrote. Play the account did itself is untouched. */
  clearHistory: (id: string) =>
    del<{ cleared: { bets: number; transactions: number; rounds: number } }>(
      `/admin/users/${id}/history`,
    ),

  /* ── Sessions ────────────────────────────────────────────────────────── */
  sessions: (query: Query) => get<Paged<AdminSession>>('/admin/sessions', query),
  revokeSession: (id: string, reason?: string) =>
    del<AdminSession>(`/admin/sessions/${id}`, { reason }),
  revokeAllSessions: (principalType: 'admin' | 'player', principalId: string, reason?: string) =>
    post<{ sessionsEnded: number }>('/admin/sessions/revoke-all', { principalType, principalId, reason }),

  /* ── Games ───────────────────────────────────────────────────────────── */
  /** Every tile, hidden ones included — this is the console's own list. */
  adminGames: () => get<{ items: AdminGame[] }>('/admin/games'),

  /**
   * Adds a tile to the hall. A new tile starts display-only on the default
   * round settings; making it playable is a separate save.
   */
  createGame: (body: GameTileChanges & { slug: string; name: string; reason?: string }) =>
    post<AdminGame>('/admin/games', body),

  updateGame: (
    id: string,
    body: GameTileChanges & {
      isRunning?: boolean;
      config?: Partial<GameConfig>;
      reason?: string;
    },
  ) => patch<AdminGame>(`/admin/games/${id}`, body),

  /**
   * Removes a tile permanently.
   *
   * Refused by the API for a game that has rounds behind it, or one the
   * scheduler is still running — hiding is the answer for those.
   */
  deleteGame: (id: string, reason?: string) =>
    del<{ deleted: boolean; id: string; slug: string; name: string }>(
      `/admin/games/${id}`,
      { reason },
    ),

  /**
   * Cancels the round counting down and opens a replacement on the current
   * settings. Every stake on the cancelled round is refunded.
   */
  voidRound: (id: string, reason: string) =>
    post<{ voided: Draw; next: Draw; refunded: number }>(`/admin/games/${id}/void-round`, {
      reason,
    }),

  /**
   * The winning numbers queued for this game's next rounds.
   *
   * The first entry belongs to the round counting down now; the rest are
   * projected from the interval. Reading it tops the queue up, so it is never
   * empty on a game that has just been created or reconfigured.
   */
  upcomingRounds: (gameId: string) =>
    get<UpcomingRounds>(`/admin/games/${gameId}/upcoming-rounds`),

  /**
   * Sets what an upcoming round will open on. Returns the whole queue back, so
   * the screen never has to guess how the edit rearranged it.
   */
  setUpcomingNumbers: (gameId: string, planId: string, numbers: number[], reason?: string) =>
    put<UpcomingRounds>(`/admin/games/${gameId}/upcoming-rounds/${planId}`, { numbers, reason }),

  /** Throws a hand-set entry away and draws a fresh one in its place. */
  regenerateUpcoming: (gameId: string, planId: string) =>
    post<UpcomingRounds>(`/admin/games/${gameId}/upcoming-rounds/${planId}/regenerate`, {}),

  /* ── Ledger ──────────────────────────────────────────────────────────── */
  transactions: (query: Query) => get<Paged<LedgerRow>>('/admin/transactions', query),
  /**
   * Edits a ledger row's annotation, its state, or when it happened. The amount
   * is immutable — it is what every cached balance is the sum of.
   */
  updateTransaction: (
    id: string,
    body: { reason?: string; status?: string; createdAt?: string },
  ) => patch<WalletTransaction>(`/admin/transactions/${id}`, body),
  deleteTransaction: (id: string, reason?: string) =>
    del<{ deleted: boolean; reference: string; balanceCents: number | null }>(
      `/admin/transactions/${id}`,
      { reason },
    ),

  /* ── Staff ───────────────────────────────────────────────────────────── */
  admins: (query: Query) => get<Paged<AdminProfile>>('/admin/admins', query),
  createAdmin: (body: { username: string; password: string; displayName?: string; role: AdminRole }) =>
    post<AdminProfile>('/admin/admins', body),
  updateAdmin: (
    id: string,
    body: { displayName?: string; role?: AdminRole; isActive?: boolean; password?: string },
  ) => patch<AdminProfile>(`/admin/admins/${id}`, body),
  deleteAdmin: (id: string) => del<{ deleted: boolean; username: string }>(`/admin/admins/${id}`),

  /* ── Own account ─────────────────────────────────────────────────────── */
  account: () => get<AdminProfile>('/admin/account'),
  changePassword: (body: { currentPassword: string; newPassword: string; confirmPassword: string }) =>
    put<{ updated: boolean }>('/admin/account/password', body),

  /* ── Bets ────────────────────────────────────────────────────────────── */
  bets: (query: Query) => get<Paged<Bet>>('/admin/bets', query),
  /**
   * Edits a placed bet. Any of position, bet type or stake may change; the API
   * re-judges the bet against the round's numbers and settles the difference.
   */
  updateBet: (id: string, changes: BetChanges, reason: string) =>
    patch<BetEditResult>(`/admin/bets/${id}`, { ...changes, reason }),
  deleteBet: (id: string, reason: string) =>
    del<{ bet: Bet; reversed: boolean }>(`/admin/bets/${id}`, { reason }),

  /* ── Draws ───────────────────────────────────────────────────────────── */
  draws: (query: Query) => get<Paged<AdminDraw> & { summary: DrawSummary }>('/admin/draws', query),
  /**
   * Sets a round's winning numbers, whether or not it has been drawn before.
   * The API re-judges every bet on the round and moves the money to match.
   */
  setResult: (id: string, numbers: number[], reason: string) =>
    put<ResultEditResult>(`/admin/draws/${id}/result`, { numbers, reason }),
  deleteDraw: (id: string, reason?: string) =>
    del<{ drawNo: number; periodCode: string; betsRemoved: number; reversed: number }>(
      `/admin/draws/${id}`,
      { reason },
    ),
  voidDraw: (id: string, reason: string) =>
    post<{ draw: Draw; refunded: number }>(`/admin/draws/${id}/void`, { reason }),

  /* ── Operations ──────────────────────────────────────────────────────── */
  auditLog: (query: Query) => get<Paged<AuditEntry>>('/admin/audit-log', query),
  report: (query: Query) => get<ReportSummary>('/admin/reports/summary', query),
  referralCodes: (query: Query) => get<Paged<ReferralCode>>('/admin/referral-codes', query),
  updateReferralCode: (
    id: string,
    body: { note?: string | null; maxUses?: number; expiresAt?: string | null; isActive?: boolean },
  ) => patch<ReferralCode>(`/admin/referral-codes/${id}`, body),
  deleteReferralCode: (id: string) =>
    del<{ deleted: boolean; code: string; signupCount: number }>(`/admin/referral-codes/${id}`),
  createReferralCode: (body: { code?: string; note?: string; maxUses: number; expiresAt?: string }) =>
    post<ReferralCode>('/admin/referral-codes', body),
  settings: () => get<Settings>('/admin/settings'),
  updateSettings: (body: Settings) => put<Settings>('/admin/settings', body),

  /**
   * The rules-and-tutorial booklet, on its own validated route.
   *
   * Not part of `updateSettings` above, which posts an unchecked key/value
   * record: this is a structured document the player app renders section by
   * section, and the API refuses it through the flat route for that reason.
   *
   * Both languages go together and replace what is stored. Publishing bumps the
   * version, which is what raises the popup again for players who have already
   * read it.
   */
  rulesGuide: () => get<RulesGuide>('/admin/settings/rules-guide'),
  updateRulesGuide: (body: { en: RulesGuideDocument; zh: RulesGuideDocument; reason?: string }) =>
    put<RulesGuide>('/admin/settings/rules-guide', body),
  system: () => get<SystemStats>('/admin/system'),

  /* ── Permissions ─────────────────────────────────────────────────────── */
  permissions: () => get<PermissionCatalogue>('/admin/permissions'),
  updatePermissions: (role: string, grid: PermissionGrid, reason?: string) =>
    put<{ role: string; grid: PermissionGrid }>(`/admin/permissions/${role}`, { grid, reason }),
  resetPermissions: (role: string) =>
    post<{ role: string; grid: PermissionGrid }>(`/admin/permissions/${role}/reset`, {}),
  clearCache: () => del<{ cleared: boolean }>('/admin/cache'),
};
